import { useState, useEffect, forwardRef } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { Slide, List } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PolicyIcon from "@mui/icons-material/Policy";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LockResetIcon from "@mui/icons-material/LockReset";
import PolicyStatusIcon from "@mui/icons-material/GppGood";
import SidebarItem from "./SidebarItem";
import { CLEAR_REDUX_STATE } from "../../../redux/auth/CommonRedux";

export const DEALER_SECTIONS = [
  {
    items: [
      {
        title: "Dashboard",
        icon: DashboardIcon,
        href: "/dashboard",
      },
      {
        title: "Punched Policy",
        icon: PolicyStatusIcon,
        href: "/policy-list",
      },
      {
        title: "Claims",
        icon: AssignmentIcon,
        href: "/claims",
      },
      {
        title: "Claim Status",
        icon: FactCheckIcon,
        href: "/claimstatus",
      },
      {
        title: "Policy Status",
        icon: PolicyIcon,
        href: "/policy-status",
      },
      {
        title: "Change Password",
        icon: LockResetIcon,
        href: "/change-password",
      },
    ],
  },
];

export const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ========================================
// Helper Functions
// ========================================

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, { path: item.href, exact: false });
    acc.push(
      <SidebarItem
        depth={depth}
        icon={item.icon}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({ depth: depth + 1, pathname, items: item.items })}
      </SidebarItem>
    );
  } else {
    acc.push(
      <SidebarItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        title={item.title}
      />
    );
  }
  return acc;
}

export function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

// ========================================
// Main Hook
// ========================================

export const useNavbarData = ({ onMobileClose, openMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLogout, setIsLogout] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname, onMobileClose, openMobile]);

  // Handle logout
  const onLogout = async () => {
    dispatch(CLEAR_REDUX_STATE.CLEAR_LOGIN_DETAILS());
    dispatch(CLEAR_REDUX_STATE.CLEAR_ALL_POLICY_COUNT_DETAILS());
    toast.success("Logout successful!");
    navigate("/");
  };

  // Toggle logout dialog
  const handleLogoutOpen = () => {
    setIsLogout(true);
  };

  const handleLogoutClose = () => {
    setIsLogout(false);
  };

  const handleLogoutConfirm = () => {
    onLogout();
  };

  return {
    location,
    isLogout,
    handleLogoutOpen,
    handleLogoutClose,
    handleLogoutConfirm,
  };
};
